import { createAsyncThunk, createSelector,createSlice } from '@reduxjs/toolkit'

import UserManagementRepository from 'services/repositories/UserManagementRepository'
import {
  AddBrandPermissionRequest,
  AILimitStatus,
  BulkCreateRolePermissionsRequest,
  CreateAILimitRequest,
  CreateOneTimeAILimitRequest,
  CreateRolePermissionRequest,
  CreateRoleRequest,
  CreateUserCountryPermissionRequest,
  CreateUserRequest,
  PaginationQuery,
  SetDefaultCountryRequest,
  UpdateAILimitRequest,
  UpdateRoleRequest,
  UpdateUserRequest,
  UpdateUserRoleRequest,
  UserManagementState} from 'types/userManagement'
import { handleApiErrors } from 'utils/errors'

const initialState: UserManagementState = {
  users: {
    status: 'initial',
    data: [],
    pagination: {
      page: 1,
      pagesTotal: 0,
      recordsTotal: 0,
      take: 10
    }
  },
  roles: {
    status: 'initial',
    data: []
  },
  permissions: {
    status: 'initial',
    data: []
  },
  brands: {
    status: 'initial',
    data: []
  },
  userCountryPermissions: {
    status: 'initial',
    data: []
  },
  aiLimitStatus: {
    status: 'initial',
    data: null
  },
  currentUser: null,
  error: null
}

export const types = {
  createUser: 'userManagement/createUser',
  getUsers: 'userManagement/getUsers',
  getAllUsers: 'userManagement/getAllUsers',
  getUserById: 'userManagement/getUserById',
  updateUser: 'userManagement/updateUser',
  updateUserRole: 'userManagement/updateUserRole',
  deleteUser: 'userManagement/deleteUser',
  addBrandPermission: 'userManagement/addBrandPermission',
  removeBrandPermission: 'userManagement/removeBrandPermission',

  createRole: 'userManagement/createRole',
  getAllRoles: 'userManagement/getAllRoles',
  getRoleById: 'userManagement/getRoleById',
  getRoleByName: 'userManagement/getRoleByName',
  updateRole: 'userManagement/updateRole',
  deleteRole: 'userManagement/deleteRole',

  createRolePermission: 'userManagement/createRolePermission',
  bulkCreateRolePermissions: 'userManagement/bulkCreateRolePermissions',
  getAllPermissions: 'userManagement/getAllPermissions',
  getAllRolePermissions: 'userManagement/getAllRolePermissions',
  getRolePermissionById: 'userManagement/getRolePermissionById',
  deleteRolePermission: 'userManagement/deleteRolePermission',

  createUserCountryPermission: 'userManagement/createUserCountryPermission',
  getUserCountryPermissions: 'userManagement/getUserCountryPermissions',
  getAllUserCountryPermissions: 'userManagement/getAllUserCountryPermissions',
  getUserCountryPermissionById: 'userManagement/getUserCountryPermissionById',
  getUserCountryPermissionsByUser:
    'userManagement/getUserCountryPermissionsByUser',
  setDefaultCountry: 'userManagement/setDefaultCountry',
  deleteUserCountryPermission: 'userManagement/deleteUserCountryPermission',
  hardDeleteUserCountryPermission:
    'userManagement/hardDeleteUserCountryPermission',

  getAllBrands: 'userManagement/getAllBrands',
  getAllBrandsForPermissionManagement:
    'userManagement/getAllBrandsForPermissionManagement',
  getBrandsList: 'userManagement/getBrandsList',
  getBrands: 'userManagement/getBrands',
  getBrandById: 'userManagement/getBrandById',

  // AI Limits
  getAILimitStatus: 'userManagement/getAILimitStatus',
  getApplicableAILimit: 'userManagement/getApplicableAILimit',
  createAILimit: 'userManagement/createAILimit',
  createOneTimeAILimit: 'userManagement/createOneTimeAILimit',
  updateAILimit: 'userManagement/updateAILimit',
  resetAILimit: 'userManagement/resetAILimit',
  getAllAILimits: 'userManagement/getAllAILimits'
} as const

export const createUser = createAsyncThunk(
  types.createUser,
  async (payload: CreateUserRequest, { rejectWithValue }) => {
    try {
      const response = await UserManagementRepository.createUser(payload)

      return response.data
    } catch (err: any) {
      console.error('UserManagementRepository.createUser error:', err)

      return rejectWithValue(err?.response?.data)
    }
  }
)

export const getUsers = createAsyncThunk(
  types.getUsers,
  async (query: PaginationQuery | undefined, { rejectWithValue }) => {
    try {
      const response = await UserManagementRepository.getUsers(query)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const getAllUsers = createAsyncThunk(
  types.getAllUsers,
  async (_, { rejectWithValue }) => {
    try {
      const response = await UserManagementRepository.getAllUsers()

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const getUserById = createAsyncThunk(
  types.getUserById,
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await UserManagementRepository.getUserById(id)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const updateUser = createAsyncThunk(
  types.updateUser,
  async (
    { id, payload }: { id: string; payload: UpdateUserRequest },
    { rejectWithValue }
  ) => {
    try {
      const response = await UserManagementRepository.updateUser(id, payload)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const updateUserRole = createAsyncThunk(
  types.updateUserRole,
  async (
    { id, payload }: { id: string; payload: UpdateUserRoleRequest },
    { rejectWithValue }
  ) => {
    try {
      const response = await UserManagementRepository.updateUserRole(
        id,
        payload
      )

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const deleteUser = createAsyncThunk(
  types.deleteUser,
  async (id: string, { rejectWithValue }) => {
    try {
      await UserManagementRepository.deleteUser(id)

      return id
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const addBrandPermission = createAsyncThunk(
  types.addBrandPermission,
  async (
    { userId, brandId }: { userId: string; brandId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await UserManagementRepository.addBrandPermission(
        userId,
        brandId
      )

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const removeBrandPermission = createAsyncThunk(
  types.removeBrandPermission,
  async (
    { userId, brandId }: { userId: string; brandId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await UserManagementRepository.removeBrandPermission(
        userId,
        brandId
      )

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const createRole = createAsyncThunk(
  types.createRole,
  async (payload: CreateRoleRequest, { rejectWithValue }) => {
    try {
      const response = await UserManagementRepository.createRole(payload)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const getAllRoles = createAsyncThunk(
  types.getAllRoles,
  async (_, { rejectWithValue }) => {
    try {
      const response = await UserManagementRepository.getAllRoles()

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const getRoleById = createAsyncThunk(
  types.getRoleById,
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await UserManagementRepository.getRoleById(id)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const getRoleByName = createAsyncThunk(
  types.getRoleByName,
  async (name: string, { rejectWithValue }) => {
    try {
      const response = await UserManagementRepository.getRoleByName(name)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const updateRole = createAsyncThunk(
  types.updateRole,
  async (
    { id, payload }: { id: string; payload: UpdateRoleRequest },
    { rejectWithValue }
  ) => {
    try {
      const response = await UserManagementRepository.updateRole(id, payload)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const deleteRole = createAsyncThunk(
  types.deleteRole,
  async (id: string, { rejectWithValue }) => {
    try {
      await UserManagementRepository.deleteRole(id)

      return id
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const getAllPermissions = createAsyncThunk(
  types.getAllPermissions,
  async (_, { rejectWithValue }) => {
    try {
      const response = await UserManagementRepository.getAllPermissions()

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const createRolePermission = createAsyncThunk(
  types.createRolePermission,
  async (payload: CreateRolePermissionRequest, { rejectWithValue }) => {
    try {
      const response = await UserManagementRepository.createRolePermission(
        payload
      )

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const bulkCreateRolePermissions = createAsyncThunk(
  types.bulkCreateRolePermissions,
  async (payload: BulkCreateRolePermissionsRequest, { rejectWithValue }) => {
    try {
      const response = await UserManagementRepository.bulkCreateRolePermissions(
        payload
      )

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const deleteRolePermission = createAsyncThunk(
  types.deleteRolePermission,
  async (id: string, { rejectWithValue }) => {
    try {
      await UserManagementRepository.deleteRolePermission(id)

      return id
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const createUserCountryPermission = createAsyncThunk(
  types.createUserCountryPermission,
  async (payload: CreateUserCountryPermissionRequest, { rejectWithValue }) => {
    try {
      const response =
        await UserManagementRepository.createUserCountryPermission(payload)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const getUserCountryPermissionsByUser = createAsyncThunk(
  types.getUserCountryPermissionsByUser,
  async (userId: string, { rejectWithValue }) => {
    try {
      const response =
        await UserManagementRepository.getUserCountryPermissionsByUser(userId)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const setDefaultCountry = createAsyncThunk(
  types.setDefaultCountry,
  async (
    { userId, payload }: { userId: string; payload: SetDefaultCountryRequest },
    { rejectWithValue }
  ) => {
    try {
      const response = await UserManagementRepository.setDefaultCountry(
        userId,
        payload
      )

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const deleteUserCountryPermission = createAsyncThunk(
  types.deleteUserCountryPermission,
  async (id: string, { rejectWithValue }) => {
    try {
      await UserManagementRepository.deleteUserCountryPermission(id)

      return id
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const getAllBrands = createAsyncThunk(
  types.getAllBrands,
  async (_, { rejectWithValue }) => {
    try {
      const response = await UserManagementRepository.getAllBrands()

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const getAllBrandsForPermissionManagement = createAsyncThunk(
  types.getAllBrandsForPermissionManagement,
  async (_, { rejectWithValue }) => {
    try {
      const response =
        await UserManagementRepository.getAllBrandsForPermissionManagement()

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const getBrandsList = createAsyncThunk(
  types.getBrandsList,
  async (_, { rejectWithValue }) => {
    try {
      const response = await UserManagementRepository.getBrandsList()

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const getBrands = createAsyncThunk(
  types.getBrands,
  async (query: PaginationQuery | undefined, { rejectWithValue }) => {
    try {
      const response = await UserManagementRepository.getBrands(query)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const getBrandById = createAsyncThunk(
  types.getBrandById,
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await UserManagementRepository.getBrandById(id)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const getAILimitStatus = createAsyncThunk(
  types.getAILimitStatus,
  async (
    params: {
      operationType:
        | 'translation'
        | 'image_text_generation'
        | 'asset_image_text_generation'
      userId: string
      brandId?: string
      countryCode?: string
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await UserManagementRepository.getAILimitStatus(params)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const getApplicableAILimit = createAsyncThunk(
  types.getApplicableAILimit,
  async (
    params: {
      operationType:
        | 'translation'
        | 'image_text_generation'
        | 'asset_image_text_generation'
      brandId?: string
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await UserManagementRepository.getApplicableAILimit(
        params
      )

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const createAILimit = createAsyncThunk(
  types.createAILimit,
  async (payload: CreateAILimitRequest, { rejectWithValue }) => {
    try {
      const response = await UserManagementRepository.createAILimit(payload)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const createOneTimeAILimit = createAsyncThunk(
  types.createOneTimeAILimit,
  async (payload: CreateOneTimeAILimitRequest, { rejectWithValue }) => {
    try {
      const response = await UserManagementRepository.createOneTimeAILimit(
        payload
      )

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const updateAILimit = createAsyncThunk(
  types.updateAILimit,
  async (
    { limitId, payload }: { limitId: string; payload: UpdateAILimitRequest },
    { rejectWithValue }
  ) => {
    try {
      const response = await UserManagementRepository.updateAILimit(
        limitId,
        payload
      )

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const resetAILimit = createAsyncThunk(
  types.resetAILimit,
  async (limitId: string, { rejectWithValue }) => {
    try {
      const response = await UserManagementRepository.resetAILimit(limitId)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const getAllAILimits = createAsyncThunk(
  types.getAllAILimits,
  async (
    params: {
      userId?: string
      type?: 'user' | 'brand_country' | 'global'
      operationType?:
        | 'translation'
        | 'image_text_generation'
        | 'asset_image_text_generation'
    } | undefined,
    { rejectWithValue }
  ) => {
    try {
      const response = await UserManagementRepository.getAllAILimits(params)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data)
    }
  }
)

export const userManagementSlice = createSlice({
  name: 'userManagement',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null
    },
    setCurrentUser: (state, action) => {
      state.currentUser = action.payload
    },
    clearCurrentUser: state => {
      state.currentUser = null
    }
  },
  extraReducers: builder => {
    builder
      .addCase(createUser.pending, state => {
        state.users.status = 'loading'
        state.error = null
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.users.status = 'loaded'
      })
      .addCase(createUser.rejected, (state, action) => {
        state.users.status = 'failed'
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      .addCase(getUsers.pending, state => {
        state.users.status = 'loading'
        state.error = null
      })
      .addCase(getUsers.fulfilled, (state, action) => {
        state.users.status = 'loaded'
        state.users.data = action.payload.data
        state.users.pagination = action.payload.meta
      })
      .addCase(getUsers.rejected, (state, action) => {
        state.users.status = 'failed'
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      .addCase(getAllUsers.pending, state => {
        state.users.status = 'loading'
        state.error = null
      })
      .addCase(getAllUsers.fulfilled, (state, action) => {
        state.users.status = 'loaded'
        state.users.data = action.payload
      })
      .addCase(getAllUsers.rejected, (state, action) => {
        state.users.status = 'failed'
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      .addCase(getUserById.pending, state => {
        state.error = null
      })
      .addCase(getUserById.fulfilled, (state, action) => {
        state.currentUser = action.payload
      })
      .addCase(getUserById.rejected, (state, action) => {
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      .addCase(updateUser.pending, state => {
        state.error = null
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        const index = state.users.data.findIndex(
          user => user.id === action.payload.id
        )

        if (index !== -1) {
          state.users.data[index] = action.payload
        }

        if (state.currentUser?.id === action.payload.id) {
          state.currentUser = action.payload
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      .addCase(updateUserRole.pending, state => {
        state.error = null
      })
      .addCase(updateUserRole.fulfilled, (state, action) => {
        const index = state.users.data.findIndex(
          user => user.id === action.payload.id
        )

        if (index !== -1) {
          state.users.data[index] = action.payload
        }

        if (state.currentUser?.id === action.payload.id) {
          state.currentUser = action.payload
        }
      })
      .addCase(updateUserRole.rejected, (state, action) => {
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      .addCase(addBrandPermission.pending, state => {
        state.error = null
      })
      .addCase(addBrandPermission.fulfilled, (state, action) => {
        state.error = null
      })
      .addCase(addBrandPermission.rejected, (state, action) => {
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      .addCase(removeBrandPermission.pending, state => {
        state.error = null
      })
      .addCase(removeBrandPermission.fulfilled, (state, action) => {
        // Brand permission removed successfully
        state.error = null
      })
      .addCase(removeBrandPermission.rejected, (state, action) => {
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      .addCase(deleteUser.pending, state => {
        state.error = null
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        if (state.currentUser?.id === action.payload) {
          state.currentUser = null
        }
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      .addCase(getUserCountryPermissionsByUser.pending, state => {
        state.userCountryPermissions.status = 'loading'
        state.error = null
      })
      .addCase(getUserCountryPermissionsByUser.fulfilled, (state, action) => {
        state.userCountryPermissions.status = 'loaded'
        state.userCountryPermissions.data = action.payload
      })
      .addCase(getUserCountryPermissionsByUser.rejected, (state, action) => {
        state.userCountryPermissions.status = 'failed'
        handleApiErrors(action, false)
      })

    builder
      .addCase(getAllRoles.pending, state => {
        state.roles.status = 'loading'
        state.error = null
      })
      .addCase(getAllRoles.fulfilled, (state, action) => {
        state.roles.status = 'loaded'
        state.roles.data = action.payload
      })
      .addCase(getAllRoles.rejected, (state, action) => {
        state.roles.status = 'failed'
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

    builder
      .addCase(getAllPermissions.pending, state => {
        state.permissions.status = 'loading'
        state.error = null
      })
      .addCase(getAllPermissions.fulfilled, (state, action) => {
        state.permissions.status = 'loaded'
        state.permissions.data = action.payload
      })
      .addCase(getAllPermissions.rejected, (state, action) => {
        state.permissions.status = 'failed'
        state.error = action.payload as string
        handleApiErrors(action, false)
      })
    builder
      .addCase(getAllBrands.pending, state => {
        state.brands.status = 'loading'
        state.error = null
      })
      .addCase(getAllBrands.fulfilled, (state, action) => {
        state.brands.status = 'loaded'
        state.brands.data = action.payload
      })
      .addCase(getAllBrands.rejected, (state, action) => {
        state.brands.status = 'failed'
        state.error = action.payload as string
        handleApiErrors(action, false)
      })
      .addCase(getAllBrandsForPermissionManagement.pending, state => {
        state.brands.status = 'loading'
        state.error = null
      })
      .addCase(getAllBrandsForPermissionManagement.fulfilled, (state, action) => {
        state.brands.status = 'loaded'
        state.brands.data = action.payload
      })
      .addCase(getAllBrandsForPermissionManagement.rejected, (state, action) => {
        state.brands.status = 'failed'
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

    builder
      .addCase(getAILimitStatus.pending, state => {
        state.aiLimitStatus.status = 'loading'
        state.error = null
      })
      .addCase(getAILimitStatus.fulfilled, (state, action) => {
        state.aiLimitStatus.status = 'loaded'
        state.aiLimitStatus.data = action.payload
      })
      .addCase(getAILimitStatus.rejected, (state, action) => {
        state.aiLimitStatus.status = 'failed'
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      .addCase(createAILimit.pending, state => {
        state.error = null
      })
      .addCase(createAILimit.fulfilled, (state, action) => {
        state.error = null
      })
      .addCase(createAILimit.rejected, (state, action) => {
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      .addCase(createOneTimeAILimit.pending, state => {
        state.error = null
      })
      .addCase(createOneTimeAILimit.fulfilled, (state, action) => {
        state.error = null
      })
      .addCase(createOneTimeAILimit.rejected, (state, action) => {
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      .addCase(updateAILimit.pending, state => {
        state.error = null
      })
      .addCase(updateAILimit.fulfilled, (state, action) => {
        state.error = null
      })
      .addCase(updateAILimit.rejected, (state, action) => {
        state.error = action.payload as string
        handleApiErrors(action, false)
      })

      .addCase(resetAILimit.pending, state => {
        state.error = null
      })
      .addCase(resetAILimit.fulfilled, (state, action) => {
        state.error = null
      })
      .addCase(resetAILimit.rejected, (state, action) => {
        state.error = action.payload as string
        handleApiErrors(action, false)
      })
  }
})

export const { clearError, setCurrentUser, clearCurrentUser } =
  userManagementSlice.actions

export const selectUserManagement = (state: any) => state.userManagement

export const selectUsers = createSelector(
  selectUserManagement,
  state => state.users
)

export const selectRoles = createSelector(
  selectUserManagement,
  state => state.roles
)

export const selectPermissions = createSelector(
  selectUserManagement,
  state => state.permissions
)

export const selectCurrentUser = createSelector(
  selectUserManagement,
  state => state.currentUser
)

export const selectUserManagementError = createSelector(
  selectUserManagement,
  state => state.error
)

export const selectBrands = createSelector(
  selectUserManagement,
  state => state.brands
)

export const selectUserCountryPermissions = createSelector(
  selectUserManagement,
  state => state.userCountryPermissions
)

export const selectAILimitStatus = createSelector(
  selectUserManagement,
  state => state.aiLimitStatus
)

export default userManagementSlice.reducer
